import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Copy, Check, ExternalLink, Heart } from "lucide-react";
import { LogoMark } from "./Logo";

const CashAppContext = createContext({ open: () => {} });
export const useCashApp = () => useContext(CashAppContext);

export const CASHTAG = "$VixPride";
export const CASHAPP_URL = "https://cash.app/$VixPride";

export const CashAppProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const open = useCallback(() => {
    setCopied(false);
    setIsOpen(true);
  }, []);
  const close = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setIsOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(CASHTAG);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = CASHTAG;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <CashAppContext.Provider value={{ open }}>
      {children}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="fixed inset-0 z-[100] flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="cashapp-modal"
          >
            <div className="absolute inset-0 bg-purple-950/40 backdrop-blur-sm" onClick={close} />
            <motion.div
              initial={{ scale: 0.92, y: 24, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.95, y: 12, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="relative w-full max-w-md rounded-3xl bg-white border border-purple-100 shadow-2xl shadow-purple-300/50 p-8"
            >
              <div className="absolute -top-20 -right-20 w-52 h-52 rounded-full bg-purple-200/60 blur-[80px] pointer-events-none" />
              <button
                onClick={close}
                data-testid="cashapp-close-btn"
                aria-label="Close"
                className="absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
              <LogoMark className="w-12 h-12" />
              <h3 className="font-display text-2xl font-extrabold text-slate-900 mt-4">Invest in Our Youth</h3>
              <p className="text-slate-500 text-sm mt-2 leading-relaxed">
                Your investment creates access — mentorship, academic support, sports, life skills,
                and opportunities families may not otherwise be able to afford.
              </p>
              <div className="mt-6 flex items-center justify-between gap-3 rounded-2xl border border-purple-200 bg-lavender-50 px-5 py-4">
                <span className="font-mono font-bold text-2xl text-purple-800" data-testid="cashapp-handle">
                  {CASHTAG}
                </span>
                <button
                  onClick={copy}
                  data-testid="cashapp-copy-btn"
                  className="inline-flex items-center gap-1.5 rounded-full border border-purple-300 px-3.5 py-1.5 text-xs font-semibold text-purple-800 hover:bg-purple-50 transition-colors"
                >
                  {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>
              <a
                href={CASHAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cashapp-open-link"
                className="btn-primary w-full py-3.5 mt-6"
              >
                <Heart className="w-4 h-4" /> Open CashApp <ExternalLink className="w-4 h-4" />
              </a>
              <p className="text-xs text-slate-400 mt-4 text-center">
                CashApp makes giving quick — tap to support today.
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </CashAppContext.Provider>
  );
};